import { useCallback, useEffect, useRef } from 'react';

/** Cierre automático de modales cloud (EvoHome / Samsung / LG) para cortar poll frecuente. */
export const CLOUD_MODAL_AUTO_CLOSE_MS = 120_000;

/**
 * Cierra el modal tras 2 min sin interacción.
 * Usar `requestClose` (mismo path que X / Back) para flush + historial.
 * Enganchar `noteActivity` en capture del contenedor del modal (pointer/wheel/key).
 */
export function useCloudModalAutoClose(
  open: boolean,
  requestClose: () => void,
  sessionKey?: string | number | null,
): { noteActivity: () => void } {
  const closeRef = useRef(requestClose);
  closeRef.current = requestClose;
  const openRef = useRef(open);
  openRef.current = open;
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const arm = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      closeRef.current();
    }, CLOUD_MODAL_AUTO_CLOSE_MS);
  }, [clearTimer]);

  const noteActivity = useCallback(() => {
    if (!openRef.current) return;
    arm();
  }, [arm]);

  useEffect(() => {
    if (!open) {
      clearTimer();
      return undefined;
    }
    arm();
    return clearTimer;
  }, [open, sessionKey, arm, clearTimer]);

  return { noteActivity };
}
